import { Text } from "@/components/ui/baseUi/text"
import { cn } from "@/modules/lib/utils"
import { FeesExplanationCoachIcon } from "@images/svgIcons/illustrations/feesExplanationCoachIcon"
import { FeesExplanationOwnerIcon } from "@images/svgIcons/illustrations/feesExplanationOwnerIcon"
import { useCurrentBox } from "@modules/box/hooks/useBox"
import { word } from "@modules/core/utils/i18n"
import { formatCurrency } from "@modules/currency/currency"
import { useLanguage } from "@modules/language/hooks/useLanguage"
import { Equal, Plus } from "lucide-react"

type FinanceFeesLayoutProps = {
  className?: string
} & React.ComponentProps<"div">

export function FinanceFeesLayout({ className, ...props }: FinanceFeesLayoutProps) {
  const language = useLanguage()
  const { result: boxId } = useCurrentBox()

  const feesStandardBankCardPercentage = 1.5
  const feesStandardBankCardFixed = 0.25
  const feesProfessionalBankCardPercentage = 1.9
  const feesProfessionalBankCardFixed = 0.25
  const feesEnglandBankCardPercentage = 2.5
  const feesEnglandBankCardFixed = 0.25
  const feesInternationalBankCardPercentage = 3.25
  const feesInternationalBankCardFixed = 0.25
  const feesSepaFixed = 0.35
  const feesStripePercentage = 0.95
  const feesStripeFixed = 0.1
  const feesHustleUpCoachPercentage = 4
  const feesHustleUpOwnerPercentage = 1

  const exampleTransactionAmount = 100
  const exampleFeesBankCardPercentageAmount = (exampleTransactionAmount * feesStandardBankCardPercentage) / 100
  const exampleFeesStripePercentageAmount = (exampleTransactionAmount * feesStripePercentage) / 100
  const exampleFeesHustleUpPercentageAmount =
    (exampleTransactionAmount * (boxId ? feesHustleUpOwnerPercentage : feesHustleUpCoachPercentage)) / 100
  const exampleFeesAmount =
    exampleFeesBankCardPercentageAmount +
    exampleFeesStripePercentageAmount +
    exampleFeesHustleUpPercentageAmount +
    feesStandardBankCardFixed +
    feesStripeFixed
  const exampleReceveidAmount = exampleTransactionAmount - exampleFeesAmount

  return (
    <div className={cn("flex w-full flex-col gap-5 px-5 pt-18 pb-10", className)} {...props}>
      <Text fontStyle="TL" className="text-textDefault-heading">
        {word("finances.feesExplanation.layout.title")}
      </Text>

      {/* REMUNERATION */}
      <section className="flex flex-col gap-2">
        <Text fontStyle="TM" className="text-textDefault-heading">
          {word("finances.feesExplanation.layout.subTitle.remuneration")}
        </Text>
        <div className="flex gap-2 border-l border-borderDefault pl-2">
          <div className="flex flex-col gap-2">
            <Text fontStyle="TS" className="text-textDefault-body">
              {word("finances.feesExplanation.layout.dotOne")}
            </Text>
            <Text fontStyle="TS" className="text-textDefault-body">
              {word("finances.feesExplanation.layout.dotTwo")}
            </Text>
            <Text fontStyle="TS" className="text-textDefault-body">
              {word("finances.feesExplanation.layout.dotThree")}
            </Text>
            <Text fontStyle="BS" className="pl-3 text-neutral-600">
              {word("finances.feesExplanation.layout.remunerationDelay")}
            </Text>
          </div>
          <div>{boxId ? <FeesExplanationOwnerIcon color={""} /> : <FeesExplanationCoachIcon color={""} />}</div>
        </div>
      </section>

      {/* FEES TRANSACTIONS */}
      <section className="flex flex-col gap-2">
        <Text fontStyle="TM" className="text-textDefault-heading">
          {word("finances.feesExplanation.layout.subTitle.feesTransactions")}
        </Text>
        <div className="flex flex-col gap-3 border-l border-borderDefault pl-2">
          <Text fontStyle="BM" className="text-neutral-600">
            {word("finances.feesExplanation.layout.text.feesTransactions")}
          </Text>
          <ul className="flex flex-col gap-2 pl-3">
            <li>
              <Text fontStyle="BM" className="text-textDefault-body">
                {word("finances.feesExplanation.layout.text.feesStandardBankCard")}{" "}
                <span className="font-bold">
                  {`${feesStandardBankCardPercentage.toString()} % +
                  ${formatCurrency(feesStandardBankCardFixed, "EUR", language)}`}
                </span>
              </Text>
            </li>
            <li>
              <Text fontStyle="BM" className="text-textDefault-body">
                {word("finances.feesExplanation.layout.text.feesProfessionalBankCard")}{" "}
                <span className="font-bold">
                  {`${feesProfessionalBankCardPercentage.toString()} % +
                  ${formatCurrency(feesProfessionalBankCardFixed, "EUR", language)}`}
                </span>
              </Text>
            </li>
            <li>
              <Text fontStyle="BM" className="text-textDefault-body">
                {word("finances.feesExplanation.layout.text.feesEnglandBankCard")}{" "}
                <span className="font-bold">{`${feesEnglandBankCardPercentage.toString()} % +
                  ${formatCurrency(feesEnglandBankCardFixed, "EUR", language)}`}</span>
              </Text>
            </li>
            <li>
              <Text fontStyle="BM" className="text-textDefault-body">
                {word("finances.feesExplanation.layout.text.feesInternationalBankCard")}{" "}
                <span className="font-bold">{`${feesInternationalBankCardPercentage.toString()} % +
                  ${formatCurrency(feesInternationalBankCardFixed, "EUR", language)}`}</span>
              </Text>
            </li>
            {boxId && (
              <li>
                <Text fontStyle="BM" className="text-textDefault-body">
                  {word("finances.feesExplanation.layout.text.feesSepa")}{" "}
                  <span className="font-bold">{formatCurrency(feesSepaFixed, "EUR", language)}</span>
                </Text>
              </li>
            )}
          </ul>
        </div>
      </section>

      {/* FEES STRIPE */}
      <section className="flex flex-col gap-2">
        <Text fontStyle="TM" className="text-textDefault-heading">
          {word("finances.feesExplanation.layout.subTitle.feesStripe")}
        </Text>
        <div className="flex flex-col gap-3 border-l border-borderDefault pl-2">
          <Text fontStyle="BM" className="text-neutral-600">
            {word("finances.feesExplanation.layout.text.feesStripe")}{" "}
            <span className="font-bold">
              {`${feesStripePercentage.toString()} % +
                ${formatCurrency(feesStripeFixed, "EUR", language)}
              ${word("finances.feesExplanation.layout.text.perTransaction")}`}
            </span>
          </Text>
          {boxId && (
            <Text fontStyle="BM" className="flex flex-col text-neutral-600">
              <span className="font-bold">{word("finances.feesExplanation.layout.text.optional_charge")}</span>
              {word("finances.feesExplanation.layout.text.country_tax")}
            </Text>
          )}
        </div>
      </section>

      {/* FEES HUSTLE UP */}
      <section className="flex flex-col gap-2">
        <Text fontStyle="TM" className="text-textDefault-heading">
          {word("finances.feesExplanation.layout.subTitle.feesHustleUp")}
        </Text>
        <div className="flex flex-col gap-2 border-l border-borderDefault pl-2">
          <Text fontStyle="BM" className="text-neutral-600">
            {word("finances.feesExplanation.layout.text.feesHustleUp")}{" "}
            <span className="font-bold">{boxId ? feesHustleUpOwnerPercentage : feesHustleUpCoachPercentage} %</span>
          </Text>
        </div>
      </section>

      {/* EXEMPLE */}
      <section className="flex flex-col gap-3 rounded-4xl bg-surface-foreground/80 p-8">
        <Text fontStyle="TS" className="text-textDefault-heading">
          {word("finances.feesExplanation.layout.subTitle.example")}
        </Text>
        <Text fontStyle="BM" className="text-neutral-600">
          {word("finances.feesExplanation.layout.text.feesExample.soldProgram.part1")}{" "}
          <span className="">{formatCurrency(exampleTransactionAmount, "EUR", language)}</span>{" "}
          {word("finances.feesExplanation.layout.text.feesExample.soldProgram.part2")}
        </Text>
        <Text fontStyle="BM" className="text-neutral-600">
          {word("finances.feesExplanation.layout.text.feesExample.receivedAmount.part1")}{" "}
          <span className="font-bold">{formatCurrency(exampleReceveidAmount, "EUR", language)}</span>{" "}
          {word("finances.feesExplanation.layout.text.feesExample.receivedAmount.part2")}{" "}
          <span className="font-bold">{formatCurrency(exampleFeesAmount, "EUR", language)}</span>
        </Text>
        <div className="flex flex-col gap-2">
          <Text fontStyle="BS" className="text-neutral-600">
            {word("finances.feesExplanation.layout.text.detailFees")}
          </Text>
          <Text fontStyle="BS" className="flex items-center gap-1 text-neutral-600">
            <Plus strokeWidth={1} color="currentColor" className="text-transparent" />
            {word("finances.feesExplanation.layout.text.feesStandardBankCardExample")}
            <span className="font-bold">{`${feesStandardBankCardPercentage.toString()} % (${formatCurrency(exampleFeesBankCardPercentageAmount, "EUR", language)}) + ${formatCurrency(feesStandardBankCardFixed, "EUR", language)}`}</span>
          </Text>
          <Text fontStyle="BS" className="flex items-center gap-1 text-neutral-600">
            <Plus strokeWidth={1} />
            {word("finances.feesExplanation.layout.subTitle.feesStripe")}{" "}
            <span className="font-bold">{`${feesStripePercentage.toString()} % (${formatCurrency(exampleFeesStripePercentageAmount, "EUR", language)}) + ${formatCurrency(feesStripeFixed, "EUR", language)}`}</span>{" "}
            {word("finances.feesExplanation.layout.text.perTransaction")}
          </Text>
          <Text fontStyle="BS" className="flex items-center gap-1 text-neutral-600">
            <Plus strokeWidth={1} />
            {word("finances.feesExplanation.layout.subTitle.feesHustleUp")}{" "}
            <span className="font-bold">{`${boxId ? feesHustleUpOwnerPercentage.toString() : feesHustleUpCoachPercentage.toString()} % (${formatCurrency(exampleFeesHustleUpPercentageAmount, "EUR", language)})`}</span>
          </Text>
          <Text fontStyle="BS" className="flex items-center gap-1 text-neutral-600">
            <Equal strokeWidth={1} />
            <span className="font-bold">{formatCurrency(exampleFeesAmount, "EUR", language)}</span>
          </Text>
        </div>
      </section>
    </div>
  )
}
